import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download, FileText, FileType2, BookOpen, FileCode, MessagesSquare, Loader2, Sparkles, AlertTriangle, Drama, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { usePW } from '@/lib/store';
import { exportApi, API_BASE } from '@/lib/api';
import { useCapabilities } from '@/lib/useCapabilities';
import ExportJobsPanel from './ExportJobsPanel';
import ScriptConverterDialog from './ScriptConverterDialog';
import BulkNovelDraftDialog from './BulkNovelDraftDialog';

// iter55g47 — Restore module-scope helpers that the book-template
// download button was referencing without importing. Reuses the
// shared `API_BASE` (BACKEND_URL + /api) so the path matches every
// other export in this dialog.
const API = API_BASE;

const FORMATS = [
  { id: 'pdf',  label: 'Adobe PDF',      ext: '.pdf',  Icon: FileText,  desc: 'Print-ready with manuscript styling',  forChat: true },
  { id: 'docx', label: 'Microsoft Word', ext: '.docx', Icon: FileType2, desc: 'Track-changes ready, Windows native',  forChat: true },
  { id: 'txt',  label: 'Plain Text',     ext: '.txt',  Icon: FileCode,  desc: 'Universal plain-text fallback',         forChat: true },
  { id: 'md',   label: 'Markdown',       ext: '.md',   Icon: FileCode,  desc: 'Plain text + structure',                forChat: false },
];

const TARGETS = [
  { id: 'novel',   label: 'Full novel',       icon: BookOpen },
  { id: 'chapter', label: 'Current chapter',  icon: FileText },
  { id: 'chat',    label: 'Conversation',     icon: MessagesSquare },
];

export function ExportDialog({ open, onOpenChange }) {
  const { project, chapters, activeChapterId } = usePW();
  const { can: capsCan, upgradeCta } = useCapabilities();
  const canBulkDraft   = capsCan('bulk_draft_novel');   // iter55g21 · Pro+
  const canScript      = capsCan('convert_to_script');  // iter55g21 · Premier
  const [pick, setPick] = useState('pdf');
  const [scope, setScope] = useState('novel');
  const [busy, setBusy] = useState(false);
  // iter55z-n — Adobe print-ready PDF option.
  const [printReady, setPrintReady] = useState(false);
  const [kdpBundle,  setKdpBundle]  = useState(false);   // iter55z-o
  const [asyncEmail, setAsyncEmail] = useState(true);   // iter55f80 · iter55g6 default-on for print-ready (prevents ingress timeouts on full novels)
  const [trim, setTrim] = useState('US_TRADE_6X9');
  const [pdfCfg, setPdfCfg] = useState({ configured: false, page_sizes: [] });
  // iter55g12 — Convert to Theatrical Script side-panel.
  const [scriptDlgOpen, setScriptDlgOpen] = useState(false);
  const [bulkDlgOpen,   setBulkDlgOpen]   = useState(false);

  const activeCh = chapters.find((c) => c.id === activeChapterId) || chapters[0];

  // Load PDF Services status once — decides whether "Print-ready" is offered.
  useEffect(() => {
    if (!open) return;
    exportApi.pdfServicesStatus()
      .then(setPdfCfg)
      .catch(() => setPdfCfg({ configured: false, page_sizes: [] }));
  }, [open]);

  const canPrintReady = scope === 'novel' && pick === 'pdf' && pdfCfg.configured;

  const run = async () => {
    if (!project) { toast.error('No project loaded'); return; }
    setBusy(true);
    try {
      if (scope === 'novel' && pick === 'pdf' && kdpBundle) {
        // iter55z-o — KDP bundle: PDF interior + cover + metadata zip
        if (asyncEmail) {
          const res = await exportApi.queueKdpBundle(project.id, trim);
          toast.success(`KDP bundle queued (#${res.job_id.slice(-6)}) — we'll email you when it's ready.`);
        } else {
          const axios = (await import('axios')).default;
          const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
          const res = await axios.post(`${API}/exports/${project.id}/kdp-bundle?page_size=${encodeURIComponent(trim)}`,
            null, { responseType: 'blob', withCredentials: true, timeout: 240000 });
          const url = URL.createObjectURL(res.data);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${(project.name || 'book').replace(/\W+/g,'_')}_kdp_bundle.zip`;
          document.body.appendChild(a); a.click();
          setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 800);
          toast.success('KDP bundle downloaded — unzip and upload interior + cover on KDP.');
        }
      } else if (scope === 'novel' && pick === 'pdf' && printReady) {
        if (asyncEmail) {
          const res = await exportApi.queueBookPdf(project.id, trim);
          toast.success(`Print-ready PDF queued (#${res.job_id.slice(-6)}) — we'll email you when it's ready.`);
        } else {
          await exportApi.printReadyBook(project.id, trim);
          toast.success(`Print-ready PDF generated (${trim.replace(/_/g, ' ')}) — check your Downloads.`);
        }
      } else if (scope === 'novel') {
        await exportApi.novel(project.id, pick);
        toast.success(`Exported as ${pick.toUpperCase()} — your browser saved the file.`);
      } else if (scope === 'chapter') {
        await exportApi.chapter(project.id, activeCh.id, pick);
        toast.success(`Exported as ${pick.toUpperCase()} — your browser saved the file.`);
      } else if (scope === 'chat') {
        await exportApi.chat(project.id, pick);
        toast.success(`Exported as ${pick.toUpperCase()} — your browser saved the file.`);
      }
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.status === 501
        ? 'Adobe PDF Services is not configured on this server.'
        : e?.response?.data?.detail || 'Export failed.';
      toast.error(detail);
    } finally { setBusy(false); }
  };

  const visibleFormats = scope === 'chat' ? FORMATS.filter((f) => f.forChat) : FORMATS;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-h-[90vh] w-[min(95vw,42rem)] max-w-[42rem] flex-col overflow-hidden p-0"
        data-testid="export-dialog"
      >
        <DialogHeader className="border-b border-border bg-panel-header/40 px-5 py-3">
          <DialogTitle className="flex items-center gap-2 text-[14px]"><Download className="h-4 w-4 text-primary" /> Export</DialogTitle>
          <DialogDescription className="sr-only">Download your novel, current chapter, or chat transcript as PDF, Word, Markdown, or plain text.</DialogDescription>
        </DialogHeader>
        {/* iter55g21 — Wrap the body in a scrollable region so tall
         *  content (all format options + KDP bundle + async email +
         *  bulk-drafter + script-converter) never clips off the viewport. */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">What to export</div>
          <div className="mt-2 grid gap-2 sm:grid-cols-3">
            {TARGETS.map((t) => {
              const Icon = t.icon;
              const active = scope === t.id;
              return (
                <button key={t.id} onClick={() => { setScope(t.id); if (t.id === 'chat' && pick === 'md') setPick('pdf'); }}
                  className={cn(
                    'flex items-center gap-2 rounded-md border p-2.5 text-left transition-all',
                    active ? 'border-primary bg-primary/5' : 'border-border bg-card hover:border-primary/50'
                  )}>
                  <Icon className={cn('h-4 w-4', active ? 'text-primary' : 'text-muted-foreground')} />
                  <span className="text-[12.5px] font-medium">{t.label}</span>
                </button>
              );
            })}
          </div>

          <div className="mt-4 text-[10.5px] uppercase tracking-wider text-muted-foreground">Format</div>
          <div className="mt-2 grid gap-2 md:grid-cols-2">
            {visibleFormats.map((f) => {
              const Icon = f.Icon;
              const active = pick === f.id;
              return (
                <button key={f.id} onClick={() => setPick(f.id)}
                  className={cn(
                    'flex items-start gap-3 rounded-md border bg-card p-3 text-left transition-all',
                    active ? 'border-primary shadow-panel-active' : 'border-border hover:border-primary/50'
                  )}>
                  <Icon className={cn('h-5 w-5 mt-0.5', active ? 'text-primary' : 'text-muted-foreground')} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[13px] font-semibold">{f.label}</span>
                      <span className="font-mono-stat text-[10.5px] text-muted-foreground">{f.ext}</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground">{f.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-3 rounded-md border border-border bg-panel-header/30 px-3 py-2 text-[10.5px] text-muted-foreground">
            The file will be generated on the server and downloaded through your browser. Your operating system’s native Save dialog will let you choose where to write the file on Windows, macOS, or Linux.
          </div>

          {/* iter55z-n — Print-ready PDF via Adobe PDF Services */}
          {scope === 'novel' && pick === 'pdf' && (
            <div
              className={cn(
                'mt-3 rounded-md border p-3 text-[11.5px] transition-colors',
                printReady ? 'border-primary bg-primary/5' : 'border-border bg-card',
              )}
              data-testid="export-print-ready-card"
            >
              <label className="flex cursor-pointer items-start gap-2">
                <input
                  type="checkbox"
                  className="mt-0.5 h-3.5 w-3.5"
                  checked={printReady}
                  disabled={!pdfCfg.configured}
                  onChange={(e) => setPrintReady(e.target.checked)}
                  data-testid="export-print-ready-toggle"
                />
                <span className="flex-1">
                  <span className="flex items-center gap-1.5 font-semibold text-foreground">
                    <Sparkles className="h-3.5 w-3.5 text-primary" />
                    Print-ready PDF (via Adobe PDF Services)
                    {pdfCfg.configured
                      ? <span className="rounded-sm bg-emerald-100 px-1.5 py-0 text-[9.5px] font-semibold uppercase tracking-wider text-emerald-800">READY</span>
                      : <span className="rounded-sm bg-amber-100 px-1.5 py-0 text-[9.5px] font-semibold uppercase tracking-wider text-amber-900">NOT CONFIGURED</span>}
                  </span>
                  <span className="mt-0.5 block text-muted-foreground">
                    Uses Adobe&apos;s server to produce a KDP / IngramSpark-ready interior PDF
                    with correct trim size, tagged PDF, and print-safe fonts. Slower than the
                    default export but yields a much cleaner file for print-on-demand.
                    {!pdfCfg.configured && (
                      <span className="mt-1 flex items-start gap-1.5 rounded-sm bg-amber-50 px-2 py-1 text-amber-900">
                        <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                        Admin: set <code className="font-mono-stat">ADOBE_PDF_CLIENT_ID</code> +{' '}
                        <code className="font-mono-stat">ADOBE_PDF_CLIENT_SECRET</code> from{' '}
                        <a className="underline" href="https://developer.adobe.com/console" target="_blank" rel="noreferrer">Adobe Developer Console</a>
                        {' '}to enable.
                      </span>
                    )}
                  </span>
                </span>
              </label>
              {printReady && canPrintReady && (
                <div className="mt-3 space-y-2 pl-6" data-testid="export-trim-selector">
                  <div>
                    <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">Trim size</div>
                    <select
                      value={trim}
                      onChange={(e) => setTrim(e.target.value)}
                      className="h-8 w-full rounded-sm border border-border bg-background px-2 text-[12px]"
                      data-testid="export-trim-select"
                    >
                      {(pdfCfg.page_sizes || []).map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.label}{p.kdp ? ' · KDP' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <label className="flex cursor-pointer items-start gap-2 rounded-sm border border-border bg-secondary/40 px-2 py-1.5 text-[11.5px]">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-3.5 w-3.5"
                      checked={kdpBundle}
                      onChange={(e) => setKdpBundle(e.target.checked)}
                      data-testid="export-kdp-bundle-toggle"
                    />
                    <span>
                      <b>KDP bundle</b> — download a zip with the PDF interior, your Canva cover (if linked),
                      and a copy-pasteable <code className="font-mono-stat">kdp_metadata.txt</code>.
                    </span>
                  </label>
                  {/* iter55f80 — Async background job with email notification */}
                  <label className="flex cursor-pointer items-start gap-2 rounded-sm border border-border bg-secondary/40 px-2 py-1.5 text-[11.5px]">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-3.5 w-3.5"
                      checked={asyncEmail}
                      onChange={(e) => setAsyncEmail(e.target.checked)}
                      data-testid="export-async-email-toggle"
                    />
                    <span>
                      <b>Render in background &amp; email me the link</b> — recommended for full novels.
                      Adobe can take 30-90&nbsp;seconds; we&apos;ll queue the job so your browser
                      doesn&apos;t time out, and email you when the file is ready.
                    </span>
                  </label>
                </div>
              )}
            </div>
          )}
          <ExportJobsPanel />
        </div>
        <DialogFooter className="flex flex-wrap items-center gap-2 border-t border-border bg-panel-header/40 px-5 py-3 sm:flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              if (!project?.id) return;
              try {
                const res = await axios.post(
                  `${API}/exports/${project.id}/book-template`,
                  {}, { withCredentials: true, responseType: 'blob', timeout: 30000 },
                );
                const url = window.URL.createObjectURL(res.data);
                const a = document.createElement('a');
                a.href = url;
                const cd = res.headers['content-disposition'] || '';
                const m = /filename="([^"]+)"/.exec(cd);
                a.download = m ? m[1] : 'book-template.docx';
                document.body.appendChild(a); a.click(); a.remove();
                window.URL.revokeObjectURL(url);
              } catch (e) {
                // eslint-disable-next-line no-alert
                alert(e?.response?.data?.detail || 'Could not export book template.');
              }
            }}
            data-testid="export-book-template-btn"
            className="gap-1.5 mr-auto"
            title="Save this novel's blueprint / bible / character skeletons / chapter outlines as a reusable .docx template."
          >
            <Download className="h-3.5 w-3.5" />
            Save as book template
          </Button>
          {/* iter55g12 · Convert to script — iter55g21 gates on `convert_to_script` (Premier). */}
          <Button
            variant="outline" size="sm"
            onClick={() => {
              if (!canScript) {
                const cta = upgradeCta('convert_to_script');
                toast.info(cta.title, {
                  description: `${cta.body} Upgrade in Settings → Subscription.`,
                  duration: 6000,
                });
                return;
              }
              setScriptDlgOpen(true);
            }}
            data-testid="export-convert-script-btn"
            data-locked={canScript ? '0' : '1'}
            className={cn('gap-1.5', !canScript && 'opacity-70')}
            title={canScript
              ? 'Adapt this novel into a Stage Play, Screenplay, or Radio Play (Fountain + .docx export).'
              : (upgradeCta('convert_to_script').body || 'Available on Premier Studio.')}
          >
            {canScript ? <Drama className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
            Convert to script…
          </Button>
          {/* iter55g18 · Bulk-draft the novel — iter55g21 gates on `bulk_draft_novel` (Pro+). */}
          <Button
            variant="outline" size="sm"
            onClick={() => {
              if (!canBulkDraft) {
                const cta = upgradeCta('bulk_draft_novel');
                toast.info(cta.title, {
                  description: `${cta.body} Upgrade in Settings → Subscription.`,
                  duration: 6000,
                });
                return;
              }
              setBulkDlgOpen(true);
            }}
            data-testid="export-bulk-draft-btn"
            data-locked={canBulkDraft ? '0' : '1'}
            className={cn('gap-1.5', !canBulkDraft && 'opacity-70')}
            title={canBulkDraft
              ? 'Have Claude read what you\'ve written and draft every remaining chapter, matching your voice.'
              : (upgradeCta('bulk_draft_novel').body || 'Available on Pro Author.')}
          >
            {canBulkDraft ? <Sparkles className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
            Draft the rest of the novel…
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" onClick={run} disabled={busy} className="gap-1.5">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            Export
          </Button>
        </DialogFooter>
      </DialogContent>
      <ScriptConverterDialog
        open={scriptDlgOpen}
        onClose={() => setScriptDlgOpen(false)}
        project={project}
      />
      <BulkNovelDraftDialog
        open={bulkDlgOpen}
        onClose={() => setBulkDlgOpen(false)}
        project={project}
      />
    </Dialog>
  );
}
